import { useMutation } from '@apollo/client'
import { Autocomplete, Popover, TextField } from '@mui/material'
import { SystemStatus } from '__generated__/graphql'
import startCase from 'lodash.startcase'
import { FC } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { UpdateSystemStatus } from 'recruiter/graphql/updateSystemStatus.gql'
import { useBulkActionContext } from 'recruiter/hooks/useBulkActionContext'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'

interface EditSystemStatusProps {
    onClose: () => void
    selectedCandidateIds: string[]
    isOpen: boolean
    anchorRef: React.RefObject<HTMLButtonElement>
}

const EditSystemStatus: FC<EditSystemStatusProps> = ({
    selectedCandidateIds,
    isOpen,
    anchorRef,
    onClose,
}) => {
    const toast = useToast()
    const { setLoading } = useBulkActionContext()
    const [updateSystemStatus] = useMutation(UpdateSystemStatus, {
        refetchQueries: [GetCandidatesForRosterSql],
    })

    return (
        <>
            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={onClose}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: getPxAsRem(32),
                        },
                    },
                }}
            >
                <Autocomplete
                    disableClearable
                    fullWidth
                    sx={{ width: getPxAsRem(224) }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            placeholder="Select Registration Status"
                        />
                    )}
                    onChange={async (event, value) => {
                        setLoading(true)
                        const { data } = await updateSystemStatus({
                            variables: {
                                candidateIds: selectedCandidateIds,
                                systemStatus: value,
                            },
                        })
                        if (data?.updateCandidateSystemStatus.success) {
                            toast({
                                title: 'Success',
                                message:
                                    data?.updateCandidateSystemStatus.message ||
                                    'Candidate registration status updated successfully',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data?.updateCandidateSystemStatus.message ||
                                    'Error updating registration status',
                                severity: 'error',
                            })
                        }
                        onClose()
                        setLoading(false)
                    }}
                    options={Object.values(SystemStatus).filter(
                        (status) => status !== SystemStatus.Null
                    )}
                    getOptionLabel={(option) => startCase(option)}
                />
            </Popover>
        </>
    )
}

export default EditSystemStatus
